// Other BC Government Services - Complete list
export interface BCService {
  id: string;
  category: string;
  name: string;
  description: string;
  whoNeedsIt: string;
  link: string;
  phone?: string;
  priority: 'high' | 'medium' | 'low'; // برای sorting
}

export const bcGovernmentServices: BCService[] = [
  // ===== IDENTITY & DOCUMENTS =====
  {
    id: 'bc_services_card',
    category: 'Identity & Documents',
    name: 'BC Services Card',
    description: 'Your official BC government ID card. Also serves as your health card.',
    whoNeedsIt: 'Everyone living in BC',
    link: 'https://www2.gov.bc.ca/gov/content/health/health-drug-coverage/msp/bc-residents/eligibility-and-enrolment/how-to-enrol/coverage-wait-period',
    priority: 'high'
  },
  {
    id: 'bc_drivers_license',
    category: 'Identity & Documents',
    name: 'BC Driver\'s License',
    description: 'Get your BC driver\'s license. Exchange your foreign license or start fresh.',
    whoNeedsIt: 'Anyone who wants to drive in BC',
    link: 'https://www.icbc.com/driver-licensing/moving-bc/Pages/Moving-to-BC.aspx',
    phone: '1-800-950-1498',
    priority: 'medium'
  },
  {
    id: 'birth_certificate',
    category: 'Identity & Documents',
    name: 'BC Birth Certificate',
    description: 'Register your child born in BC and get their birth certificate.',
    whoNeedsIt: 'Parents of children born in BC',
    link: 'https://www2.gov.bc.ca/gov/content/life-events/birth-adoption',
    priority: 'low'
  },

  // ===== FAMILY & CHILDREN =====
  {
    id: 'child_care_subsidy',
    category: 'Family & Children',
    name: 'Affordable Child Care Benefit',
    description: 'Get help paying for licensed child care. Reduced fees for eligible families.',
    whoNeedsIt: 'Families with young children in child care',
    link: 'https://www2.gov.bc.ca/gov/content/family-social-supports/caring-for-young-children/child-care-funding',
    priority: 'high'
  },
  {
    id: 'canada_child_benefit',
    category: 'Family & Children',
    name: 'Canada Child Benefit (CCB)',
    description: 'Monthly tax-free payment to help with the cost of raising children under 18.',
    whoNeedsIt: 'All families with children',
    link: 'https://www.canada.ca/en/revenue-agency/services/child-family-benefits/canada-child-benefit-overview.html',
    priority: 'high'
  },
  {
    id: 'school_registration',
    category: 'Family & Children',
    name: 'Public School Registration',
    description: 'Register your children for free public school (K-12).',
    whoNeedsIt: 'Families with school-age children',
    link: 'https://www2.gov.bc.ca/gov/content/education-training/k-12/support/welcome',
    priority: 'high'
  },
  {
    id: 'bc_family_benefit',
    category: 'Family & Children',
    name: 'BC Family Benefit',
    description: 'Monthly payment for low to moderate income families.',
    whoNeedsIt: 'Families with children under 18',
    link: 'https://www2.gov.bc.ca/gov/content/taxes/income-taxes/personal/credits/family-benefit',
    priority: 'medium'
  },

  // ===== FINANCIAL & TAX =====
  {
    id: 'income_tax',
    category: 'Financial & Tax',
    name: 'Income Tax Filing',
    description: 'File your Canadian income taxes. Free tax clinics available for low income.',
    whoNeedsIt: 'Everyone who earned income in Canada',
    link: 'https://www.canada.ca/en/services/taxes/income-tax.html',
    priority: 'high'
  },
  {
    id: 'gst_credit',
    category: 'Financial & Tax',
    name: 'GST/HST Credit',
    description: 'Tax-free quarterly payment to help with sales tax. Automatic when you file taxes.',
    whoNeedsIt: 'Low and modest income individuals and families',
    link: 'https://www.canada.ca/en/revenue-agency/services/child-family-benefits/goods-services-tax-harmonized-sales-tax-gst-hst-credit.html',
    priority: 'medium'
  },
  {
    id: 'climate_action_credit',
    category: 'Financial & Tax',
    name: 'BC Climate Action Tax Credit',
    description: 'Quarterly payment to help offset carbon tax.',
    whoNeedsIt: 'Low to moderate income BC residents',
    link: 'https://www2.gov.bc.ca/gov/content/taxes/income-taxes/personal/credits/climate-action',
    priority: 'low'
  },

  // ===== HOUSING & TRANSPORTATION =====
  {
    id: 'rental_assistance',
    category: 'Housing & Transportation',
    name: 'BC Rental Assistance Program',
    description: 'Monthly subsidy to help with rent costs for eligible low-income families.',
    whoNeedsIt: 'Low income families with children',
    link: 'https://www.bchousing.org/housing-assistance/rental-assistance-financial-aid/RAP',
    priority: 'high'
  },
  {
    id: 'translink',
    category: 'Housing & Transportation',
    name: 'TransLink Public Transit',
    description: 'Buses, SkyTrain, SeaBus in Metro Vancouver. Get a Compass Card.',
    whoNeedsIt: 'Anyone using public transit in Metro Vancouver',
    link: 'https://www.translink.ca',
    phone: '604-953-3333',
    priority: 'medium'
  },
  {
    id: 'bc_transit',
    category: 'Housing & Transportation',
    name: 'BC Transit',
    description: 'Public transit in communities outside Metro Vancouver.',
    whoNeedsIt: 'Anyone using public transit outside Vancouver',
    link: 'https://www.bctransit.com',
    priority: 'medium'
  },

  // ===== EMPLOYMENT & EDUCATION =====
  {
    id: 'employment_insurance',
    category: 'Employment & Education',
    name: 'Employment Insurance (EI)',
    description: 'Temporary income support if you lose your job. Also maternity/parental benefits.',
    whoNeedsIt: 'Workers who lose their job or need parental leave',
    link: 'https://www.canada.ca/en/services/benefits/ei.html',
    phone: '1-800-206-7218',
    priority: 'high'
  },
  {
    id: 'workbc_training',
    category: 'Employment & Education',
    name: 'WorkBC Skills Training',
    description: 'Funding for job training and skills upgrading.',
    whoNeedsIt: 'Unemployed or underemployed workers',
    link: 'https://www.workbc.ca/Training-Education/Fund-your-Training.aspx',
    priority: 'medium'
  },
  {
    id: 'student_loans',
    category: 'Employment & Education',
    name: 'BC Student Loans',
    description: 'Financial aid for post-secondary education.',
    whoNeedsIt: 'Students in college or university',
    link: 'https://studentaidbc.ca',
    priority: 'low'
  },

  // ===== HEALTH & WELLNESS =====
  {
    id: 'pharmacare',
    category: 'Health & Wellness',
    name: 'BC PharmaCare',
    description: 'Help with prescription drug costs. Fair PharmaCare based on income.',
    whoNeedsIt: 'Anyone with prescription medication costs',
    link: 'https://www2.gov.bc.ca/gov/content/health/health-drug-coverage/pharmacare-for-bc-residents',
    priority: 'medium'
  },
  {
    id: 'mental_health',
    category: 'Health & Wellness',
    name: 'Mental Health Services',
    description: 'Free counselling and mental health support through public health system.',
    whoNeedsIt: 'Anyone needing mental health support',
    link: 'https://www2.gov.bc.ca/gov/content/health/managing-your-health/mental-health-substance-use',
    phone: '1-800-SUICIDE (1-800-784-2433)',
    priority: 'high'
  },
  {
    id: 'public_health',
    category: 'Health & Wellness',
    name: 'Public Health Services',
    description: 'Immunizations, sexual health, baby wellness checks, and more.',
    whoNeedsIt: 'Everyone, especially families with children',
    link: 'https://www2.gov.bc.ca/gov/content/health/about-bc-s-health-care-system/partners/health-authorities/regional-health-authorities',
    priority: 'medium'
  },

  // ===== LEGAL & SOCIAL =====
  {
    id: 'legal_aid',
    category: 'Legal & Social',
    name: 'Legal Aid BC',
    description: 'Free or low-cost legal help for people who can\'t afford a lawyer.',
    whoNeedsIt: 'Low income people with legal problems',
    link: 'https://legalaid.bc.ca',
    phone: '1-866-577-2525',
    priority: 'medium'
  },
  {
    id: 'bc_211',
    category: 'Legal & Social',
    name: 'BC 211',
    description: 'Free help finding community, social, health and government services. Call 211.',
    whoNeedsIt: 'Anyone looking for help or information',
    link: 'https://bc.211.ca',
    phone: '211',
    priority: 'high'
  },
  {
    id: 'citizenship',
    category: 'Legal & Social',
    name: 'Canadian Citizenship',
    description: 'Apply to become a Canadian citizen after living as PR for 3 years.',
    whoNeedsIt: 'Permanent Residents eligible for citizenship',
    link: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/canadian-citizenship/become-canadian-citizen.html',
    priority: 'low'
  },

  // ===== EMERGENCY & ESSENTIAL =====
  {
    id: 'food_banks',
    category: 'Emergency & Essential',
    name: 'Food Banks',
    description: 'Free food for those in need. No ID required, just show up.',
    whoNeedsIt: 'Anyone struggling to afford food',
    link: 'https://www.foodbanksbc.ca',
    priority: 'high'
  },
  {
    id: 'crisis_lines',
    category: 'Emergency & Essential',
    name: 'Crisis & Helplines',
    description: 'Immediate help: 911 emergency, 211 info, 1-800-SUICIDE, Kids Help Phone.',
    whoNeedsIt: 'Anyone in crisis or emergency',
    link: 'https://crisiscentre.bc.ca',
    phone: '1-800-SUICIDE (1-800-784-2433)',
    priority: 'high'
  }
];

// Helper: Get services by category
export function getServicesByCategory() {
  const categories = new Map<string, BCService[]>();
  
  bcGovernmentServices.forEach(service => {
    if (!categories.has(service.category)) {
      categories.set(service.category, []);
    }
    categories.get(service.category)!.push(service);
  });
  
  return categories;
}

// Helper: Get high priority services
export function getHighPriorityServices() {
  return bcGovernmentServices.filter(s => s.priority === 'high');
}
